import React, { useCallback, useEffect, useState } from "react";

import { SimpleCrudSection } from "../components/SimpleCrudSection";
import { UsersSection } from "../components/UsersSection";
import { api } from "../lib/api";
import { Channel, Pipeline, Product, UserMini } from "../types/crm";

export function SettingsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [users, setUsers] = useState<UserMini[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [productsResponse, channelsResponse, pipelinesResponse, usersResponse] =
        await Promise.all([
          api.get<Product[]>("/products/"),
          api.get<Channel[]>("/channels/"),
          api.get<Pipeline[]>("/pipelines/"),
          api.get<UserMini[]>("/users/"),
        ]);
      setProducts(productsResponse.data);
      setChannels(channelsResponse.data);
      setPipelines(pipelinesResponse.data);
      setUsers(usersResponse.data);
    } catch {
      setError("Não foi possível carregar as configurações.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <div className="p-8">
      <h1 className="text-xl font-semibold text-slate-800">Configurações</h1>

      {isLoading && <p className="mt-6 text-sm text-slate-500">Carregando...</p>}

      {!isLoading && error && (
        <div className="mt-6 rounded-[10px] border border-border-subtle bg-surface p-4">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={fetchAll}
            className="mt-3 rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {!isLoading && !error && (
        <div className="mt-6 space-y-8">
          <SimpleCrudSection
            title="Produtos"
            apiPath="/products/"
            itemLabel="produto"
            items={products}
            allowDelete
            onItemsChange={setProducts}
          />
          <SimpleCrudSection
            title="Canais"
            apiPath="/channels/"
            itemLabel="canal"
            items={channels}
            allowDelete
            onItemsChange={setChannels}
          />

          <div>
            <h2 className="text-lg font-semibold text-slate-800">Pipelines</h2>
            <div className="mt-3 space-y-2">
              {pipelines.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between rounded-[10px] border border-border-subtle bg-white p-3"
                >
                  <div>
                    <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                      {p.name}
                      {p.is_default && (
                        <span className="rounded-full bg-primary-soft px-2 py-0.5 text-[10px] font-medium text-primary">
                          padrão
                        </span>
                      )}
                      {p.has_contract && (
                        <span className="rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-medium text-[#92400E]">
                          contrato + farol
                        </span>
                      )}
                    </p>
                    {p.context && <p className="mt-0.5 text-xs text-slate-500">{p.context}</p>}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Novos pipelines são criados pelo botão "Criar novo pipeline" na tela Pipeline.
            </p>
          </div>

          <UsersSection users={users} onUsersChange={setUsers} />
        </div>
      )}
    </div>
  );
}
